import type { FeedbackRTValue, IArea } from '@vedalib/editor/lib/elements'
import { FeedbackMode } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useUpdateEffect } from 'ahooks'
import cn from 'classnames'
import lodash from 'lodash'
import * as R from 'ramda'
import { useState } from 'react'

import { useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'

import { SHELL_SIZE_KEYS } from 'components/Editor/types/data.constants'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { setQuestion } from 'services/Store/Project/actions'
import { BlockMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlock, getBlockState, getSection } from 'services/Store/Project/selectors'
import { useUrlByUsage } from 'utils/files'

import ResponsiveImage from '../../ResponsiveImage'
import TestFeedback from '../../courseKit/TestFeedback'
import { useElementRootStyles } from '../../hooks/useElementRootStyles'
import { getFeedbackValue } from '../../utils/getFeedbackValue'
import * as s from './TestHotspotElement.module.scss'
import HotspotArea from './components/HotspotArea'
import ResizerHandlers from './components/ResizerHandlers'
import Spot from './components/Spot'
import { useHotspotActions } from './hooks/useHotspotActions'
import type { TestHotspotElementType } from './testHotspotTypes'

const TestHotspotElement: TestHotspotElementType = ({
  mode,
  element,
  block,
  styles,
  onChange,
  state,
  font,
  feedback,
  isV2,
}) => {
  const image = element.value.image
  // const file = useImageWithCrop(image)
  const url = useUrlByUsage(image, FileUsageTypeEnum.image)
  const spots = state?.spots || []
  const dispatch = useProjectDispatch()
  const [imgEl, setImgEl] = useState<HTMLImageElement | null>(null)
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null)
  const {
    onMouseDown,
    onResizeMouseDown,
    onPreviewMouseDown,
    onExistingAreaMouseDown,
    isActiveElement,
    activeSpot,
    areas,
    setAreas,
    setActiveSpot,
  } = useHotspotActions({
    state,
    element,
    setSelectedAreaId,
    img: imgEl,
    block,
    onChange,
  })
  const { generalFbValid } = state || {}
  const feedbackValue = getFeedbackValue(element.value)

  const { activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId: element.id,
    mode,
  })

  const isEditor = mode.previewMode === PreviewMode.editor
  const shellStyles = {
    maxWidth: '100%',
    maxHeight: '100%',
    ...lodash.pick(styles.shell, SHELL_SIZE_KEYS),
    ...(image?.params?.round && { borderRadius: '50%' }),
  }
  const imageStyles = useElementRootStyles(styles.image, shellStyles, styles.effects)
  const result = useProjectContext(getBlockState, block?.uuid)?.result
  const feedbackMode =
    (useProjectContext(getSection)?.test?.feedbackMode as FeedbackMode) ?? undefined
  const blockData = useProjectContext(getBlock)
  const isDetailedFeedback = blockData?.mode === BlockMode.end
  const showFinalFb = isDetailedFeedback || feedbackMode === FeedbackMode.SHOW
  const hideFeedbackByMode = feedbackMode === FeedbackMode.HIDE_ICON && !showFinalFb
  const displayIcon = feedback?.common?.displayIcon ?? true
  const hideFeedbackByIcon = !displayIcon && !isDetailedFeedback
  const answerFeedback = element.value.answerFeedback ?? true
  const showFeedbackIcon =
    isDetailedFeedback || (!!result && answerFeedback && !hideFeedbackByMode && !hideFeedbackByIcon)

  const onDeleteSpot = (id: string) => {
    dispatch(
      setQuestion({
        elementId: element.id,
        value: {
          spots: spots.filter((spot) => spot.id !== id),
        },
        isReady: true,
        blockId: block?.uuid || '',
      }),
    )
  }

  const handleChangeGeneralFeedback = (newFeedbackValue: FeedbackRTValue) => {
    onChange?.(R.assoc('generalFeedback', newFeedbackValue, element.value))
  }

  const announce = useLiveFeedback('polite')

  useUpdateEffect(() => {
    if (!result) {
      return
    }
    announce(result.isValid ? 'Correct' : 'Incorrect')
  }, [result])

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(s.root, {
          [s.preview]: !isEditor,
          [s.disabled]: !isActiveElement && isEditor,
          [s.v2]: isV2,
        })}
        onMouseDown={!isEditor ? onPreviewMouseDown : onMouseDown}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={(ref) => setImgEl(ref as any)}
      >
        <ResponsiveImage
          overlay={element.value.overlay}
          style={{
            ...imageStyles,
            borderRadius: url ? imageStyles?.borderRadius : 'none',
            objectFit: image?.objectFit,
          }}
          usage={image}
        />
        {!isEditor &&
          spots.map((spot) => (
            <Spot
              active={activeSpot === spot.id}
              deviceMode={mode.deviceMode}
              enableDelete={!result && element.value.response === 'all'}
              feedback={feedback}
              hasIcon={element.value.type === 'icon'}
              key={spot.id}
              onDeleteSpot={onDeleteSpot}
              result={result}
              setActiveSpot={setActiveSpot}
              showFeedbackIcon={showFeedbackIcon}
              spot={spot}
              styles={styles}
            />
          ))}
        <div
          className={cn({
            [s.hidden]: !isEditor && !showFeedbackIcon,
            [s.disabled]: !isEditor,
          })}
        >
          {areas?.map((area: IArea) => (
            <HotspotArea
              animate={showFeedbackIcon && !isEditor}
              area={area}
              enableDelete={areas.length > 1}
              key={area.id}
              onMouseDown={(e) => onExistingAreaMouseDown(e, area)}
              selected={selectedAreaId === area.id}
              setAreas={setAreas}
              styles={styles.area}
            >
              <ResizerHandlers
                area={area}
                onResizeMouseDown={onResizeMouseDown}
                selected={selectedAreaId === area.id}
              />
            </HotspotArea>
          ))}
        </div>
      </div>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={isEditor ? generalFbValid : result?.isValid}
        mode={mode}
        onChange={handleChangeGeneralFeedback}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: element.value.generalFeedback,
        }}
        showFeedback={element.value.displayFeedback}
        styles={feedback}
      />
    </>
  )
}

export default TestHotspotElement
